import { useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { PostgrestError } from '@supabase/supabase-js';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { handleQueryError } from '@/lib/query-client';

// Base types for Supabase operations
type SupabaseResponse<T> = {
  data: T | null;
  error: PostgrestError | null;
};

type SupabaseStorageResponse = {
  data: { path: string } | null;
  error: Error | null;
};

type SupabaseId = string | number;

type InsertableRecord = Record<string, unknown>;
type UpdatableRecord = InsertableRecord & { id: SupabaseId };
type UpsertableRecord = InsertableRecord;

// File upload types
type FileUploadVariables = {
  file: File;
  path: string;
  options?: { upsert?: boolean };
};

type FileUploadResult = {
  path: string;
  fullPath: string;
};

// Batch mutation types
type BatchMutationFunction<TVariables> = (variables: TVariables) => Promise<SupabaseResponse<unknown>>;
type BatchMutationResult<TData> = Array<TData>;

/**
 * Supabase 뮤테이션을 위한 커스텀 훅의 옵션 타입
 */
export interface UseSupabaseMutationOptions<TData = unknown, TError = PostgrestError, TVariables = unknown>
  extends Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> {
  invalidateQueries?: readonly unknown[][];
}

/**
 * Supabase 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseMutation<TData = unknown, TError = PostgrestError, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<SupabaseResponse<TData> | TData>,
  options: UseSupabaseMutationOptions<TData, TError, TVariables> = {}
): UseMutationResult<TData, TError, TVariables> {
  const queryClient = useQueryClient();
  const { invalidateQueries, ...mutationOptions } = options;

  return useMutation({
    mutationFn: async (variables: TVariables) => {
      try {
        const result = await mutationFn(variables);
        
        // Type guard for Supabase response
        if (result && typeof result === 'object' && 'error' in result && result.error) {
          throw result.error;
        }
        
        // Return data if it's a Supabase response, otherwise return the result directly
        return (result && typeof result === 'object' && 'data' in result) ? result.data : result;
      } catch (error) {
        console.error('Supabase mutation error:', error);
        throw error;
      }
    },
    onSuccess: (data, variables, context) => {
      if (invalidateQueries) {
        invalidateQueries.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
      
      options.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      console.error('Mutation failed:', error);
      options.onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
}

/**
 * INSERT 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseInsert<TData = unknown, TVariables = InsertableRecord>(
  table: string,
  options: UseSupabaseMutationOptions<TData, PostgrestError, TVariables> = {}
) {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<TData, PostgrestError, TVariables>(
    (data: TVariables) => supabase.from(table).insert(data).select().single(),
    options
  );
}

/**
 * UPDATE 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseUpdate<TData = unknown, TVariables extends UpdatableRecord = UpdatableRecord>(
  table: string,
  options: UseSupabaseMutationOptions<TData, PostgrestError, TVariables> = {}
) {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<TData, PostgrestError, TVariables>(
    ({ id, ...data }: TVariables) => 
      supabase.from(table).update(data).eq('id', id).select().single(),
    options
  );
}

/**
 * DELETE 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseDelete<TData = unknown>(
  table: string,
  options: UseSupabaseMutationOptions<TData, PostgrestError, SupabaseId> = {}
) {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<TData, PostgrestError, SupabaseId>(
    (id: SupabaseId) => supabase.from(table).delete().eq('id', id).select().single(),
    options
  );
}

/**
 * UPSERT 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseUpsert<TData = unknown, TVariables = UpsertableRecord>(
  table: string,
  options: UseSupabaseMutationOptions<TData, PostgrestError, TVariables> & {
    onConflict?: string;
  } = {}
) {
  const { supabase } = useSupabaseConnection();
  const { onConflict, ...mutationOptions } = options;
  
  return useSupabaseMutation<TData, PostgrestError, TVariables>(
    (data: TVariables) => {
      const query = supabase.from(table).upsert(data, { onConflict });
      return query.select().single();
    },
    mutationOptions
  );
}

/**
 * 파일 업로드 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseUpload(
  bucket: string,
  options: UseSupabaseMutationOptions<FileUploadResult, Error, FileUploadVariables> = {}
) {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<FileUploadResult, Error, FileUploadVariables>(
    async ({ file, path, options: uploadOptions }) => {
      const { data, error }: SupabaseStorageResponse = await supabase.storage
        .from(bucket)
        .upload(path, file, uploadOptions);
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error('Upload failed: No data returned');
      }
      
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);
      
      return {
        path: data.path,
        fullPath: publicUrl,
      };
    },
    options
  );
}

/**
 * 파일 삭제 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseDeleteFile(
  bucket: string,
  options: UseSupabaseMutationOptions<{ data: Array<{ name: string }> | null; error: Error | null }, Error, string | string[]> = {}
) {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<{ data: Array<{ name: string }> | null; error: Error | null }, Error, string | string[]>(
    async (paths: string | string[]) => {
      const pathArray = Array.isArray(paths) ? paths : [paths];
      return await supabase.storage.from(bucket).remove(pathArray);
    },
    options
  );
}

/**
 * 에러 처리가 포함된 Supabase 뮤테이션 훅
 */
export function useSupabaseMutationWithErrorHandling<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<SupabaseResponse<TData> | TData>,
  options: UseSupabaseMutationOptions<TData, PostgrestError, TVariables> & {
    onError?: (error: string) => void;
  } = {}
) {
  const { onError, ...mutationOptions } = options;
  
  return useSupabaseMutation<TData, PostgrestError, TVariables>(
    mutationFn,
    {
      ...mutationOptions,
      onError: (error, variables, context) => {
        const errorMessage = handleQueryError(error);
        onError?.(errorMessage);
        mutationOptions.onError?.(error, variables, context);
      },
    }
  );
}

/**
 * 낙관적 업데이트를 위한 뮤테이션 훅
 */
export function useSupabaseMutationWithOptimisticUpdate<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<SupabaseResponse<TData> | TData>,
  queryKey: readonly unknown[],
  updateFn: (oldData: TData, variables: TVariables) => TData,
  options: UseSupabaseMutationOptions<TData, PostgrestError, TVariables> = {}
) {
  const queryClient = useQueryClient();
  
  return useSupabaseMutation<TData, PostgrestError, TVariables>(
    mutationFn,
    {
      ...options,
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey });
        
        const previousData = queryClient.getQueryData<TData>(queryKey);
        
        if (previousData) {
          queryClient.setQueryData(queryKey, updateFn(previousData, variables));
        }
        
        options.onMutate?.(variables);
        
        return { previousData };
      },
      onError: (error, variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
        
        options.onError?.(error, variables, context);
      },
      onSettled: (data, error, variables, context) => {
        queryClient.invalidateQueries({ queryKey });
        
        options.onSettled?.(data, error, variables, context);
      },
    }
  );
}

/**
 * 배치 뮤테이션을 위한 커스텀 훅
 */
export function useSupabaseBatchMutation<TData = unknown, TVariables = unknown>(
  mutationFns: Array<BatchMutationFunction<TVariables>>,
  options: UseSupabaseMutationOptions<BatchMutationResult<TData>, PostgrestError, TVariables> = {}
) {
  return useSupabaseMutation<BatchMutationResult<TData>, PostgrestError, TVariables>(
    async (variables: TVariables) => {
      const results = await Promise.allSettled(
        mutationFns.map(fn => fn(variables))
      );
      
      const successResults = results
        .filter((result): result is PromiseFulfilledResult<SupabaseResponse<TData>> => 
          result.status === 'fulfilled' && result.value && !result.value.error
        )
        .map(result => result.value.data)
        .filter((data): data is TData => data !== null);
      
      const errors = results
        .filter((result): result is PromiseFulfilledResult<SupabaseResponse<TData>> => 
          result.status === 'fulfilled' && result.value && !!result.value.error
        )
        .map(result => result.value.error)
        .filter((error): error is PostgrestError => error !== null);
      
      if (errors.length > 0) {
        throw errors[0];
      }
      
      return successResults;
    },
    options
  );
}